import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('cart:add-start', this.#handleCartAddStart);
    document.addEventListener('cart:add-end', this.#handleCartAddEnd);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener('cart:add-start', this.#handleCartAddStart);
    document.removeEventListener('cart:add-end', this.#handleCartAddEnd);
    clearTimeout(this.#busyTimeout);
    this.#historyAbortController?.abort();
  }

  /**
   * After a bfcache restore (mobile back/forward navigation) the restored DOM
   * may predate cart changes made on other pages: mark it as suspect — the
   * next open stays masked until verified — and start a re-sync right away.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    this.#domSuspect = true;

    if (this.refs.dialog?.open) {
      this.refs.dialog.setAttribute('aria-busy', 'true');
      this.#syncWithServer().finally(this.#releaseBusy);
    } else {
      this.#syncWithServer();
    }
  };

  /** @type {boolean} */
  #domSuspect = false;

  /**
   * Verifies the drawer content on open. When the DOM is suspect (bfcache
   * restore, unconfirmed add), mask the content until the verification
   * settles so stale content is never readable; otherwise verify silently.
   */
  #handleDialogOpen = () => {
    const mustMask = this.#domSuspect;

    if (mustMask) this.refs.dialog?.setAttribute('aria-busy', 'true');

    this.#syncWithServer().finally(() => {
      if (mustMask) this.#releaseBusy();
    });
  };

  /** @type {number | undefined} */
  #busyTimeout;

  /** @type {number} */
  #pendingAddCount = 0;

  /** @type {boolean} */
  #batchHadConcurrency = false;

  /** Shows a busy overlay while add-to-cart requests are in flight. */
  #handleCartAddStart = () => {
    // Overlapping adds (mobile double-tap): one confirmed response must not
    // vouch for the whole batch — keep the batch suspect until the final
    // verification.
    if (this.#pendingAddCount > 0) this.#batchHadConcurrency = true;

    this.#pendingAddCount += 1;

    // Until this add is confirmed (CartAddEvent carrying valid drawer HTML)
    // or a re-sync completes, the drawer DOM can no longer be trusted: a
    // lost response would leave it stale without any error surfacing.
    this.#domSuspect = true;

    this.refs.dialog?.setAttribute('aria-busy', 'true');

    // Safety net: never leave the overlay up if requests hang.
    clearTimeout(this.#busyTimeout);
    this.#busyTimeout = setTimeout(() => {
      this.#pendingAddCount = 0;
      this.#releaseBusy();
    }, 10000);
  };

  #handleCartAddEnd = () => {
    if (this.#pendingAddCount > 0) this.#pendingAddCount -= 1;

    // Another add is still in flight: keep the overlay up for it.
    if (this.#pendingAddCount > 0) return;

    clearTimeout(this.#busyTimeout);
    this.#batchHadConcurrency = false;

    if (this.#domSuspect) {
      // No confirmed render for this batch (error, lost response, missing
      // sections): hold the overlay until the server verification settles.
      this.#syncWithServer().finally(this.#releaseBusy);
    } else {
      // Confirmed render: unmask immediately, then verify silently in the
      // background (deduplicated with the sync triggered by auto-open).
      this.#releaseBusy();
      this.#syncWithServer();
    }
  };

  /** Removes the busy overlay unless add requests are still in flight. */
  #releaseBusy = () => {
    if (this.#pendingAddCount > 0) return;
    this.refs.dialog?.removeAttribute('aria-busy');
  };

  /** @type {Promise<void> | null} */
  #syncPromise = null;

  /** Deduplicates concurrent sync requests (drawer open + add-end + fallback). */
  #syncWithServer = () => {
    this.#syncPromise ??= this.#performSync().finally(() => {
      this.#syncPromise = null;
    });

    return this.#syncPromise;
  };

  /**
   * The drawer DOM is only updated through cart events; a lost response or a
   * stale bfcache restore leaves it showing the wrong content (e.g. "empty
   * cart" after a successful add). Compare the rendered lines against the
   * server cart and re-render the section when they differ.
   */
  #performSync = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return;
      const cart = await response.json();

      // Signature = total quantity + sorted "key@quantity" pairs: detects
      // count changes, content swaps and per-line quantity redistributions.
      const serverPairs = (cart.items ?? []).map(
        (/** @type {{ key: string, quantity: number }} */ item) => `${item.key}@${item.quantity}`
      );
      const domPairs = Array.from(this.querySelectorAll('.cart-items__table-row[data-key]'), (row) => {
        const quantity = row.querySelector('input[name="updates[]"]')?.getAttribute('value');
        return `${row.getAttribute('data-key')}@${quantity ?? '?'}`;
      });

      // Rows without a quantity input (e.g. bundle components) can't expose
      // their quantity: fall back to comparing line keys only.
      const withQuantities = !domPairs.some((pair) => pair.endsWith('@?'));
      const toSignature = (/** @type {string[]} */ pairs) =>
        pairs
          .map((pair) => (withQuantities ? pair : pair.slice(0, pair.lastIndexOf('@'))))
          .sort()
          .join(',');

      const domCount = parseInt(this.querySelector('[ref="cartItemCount"]')?.textContent ?? '0', 10);
      const serverSignature = `${cart.item_count}|${toSignature(serverPairs)}`;
      const domSignature = `${domCount}|${toSignature(domPairs)}`;

      if (serverSignature === domSignature) {
        this.#domSuspect = false;
        return;
      }

      const sectionId = this.querySelector('cart-items-component')?.getAttribute('data-section-id');
      if (!sectionId) return;

      await sectionRenderer.renderSection(sectionId, { cache: false });

      this.#domSuspect = false;
    } catch {
      // Offline or request aborted: keep the current drawer state so the
      // next open re-verifies (#domSuspect stays raised on suspect paths).
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean, sections?: Record<string, string> } }>} event
   */
  #handleCartAdd = (event) => {
    // A rejected add (sold out, quantity limit, rate limit) must not open the
    // drawer over its "empty cart" state: the error message shown under the
    // add-to-cart button would be hidden behind the drawer.
    if (event.detail?.data?.didError) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // The add succeeded but the response carried no usable section HTML for
    // this drawer — Shopify can return `sections: null` or, per section,
    // `sections: { "<id>": null }` even on success. The drawer would open on
    // stale content while the fallback render is in flight: mask it and heal.
    const sectionId = this.querySelector('cart-items-component')?.getAttribute('data-section-id');
    const sectionHtml = sectionId ? event.detail?.data?.sections?.[sectionId] : null;
    if (!sectionHtml) {
      this.refs.dialog?.setAttribute('aria-busy', 'true');
      this.#syncWithServer().finally(this.#releaseBusy);
    } else if (!this.#batchHadConcurrency) {
      // Valid drawer HTML: the synchronous morph runs in this same event
      // dispatch, before the deferred showModal — the DOM is trustworthy.
      // (With overlapping adds, one confirmed response can't vouch for the
      // batch: the final add-end then runs a masked verification instead.)
      this.#domSuspect = false;
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
